// Composer Mode chip (Autonomous / Plan) — restored 2026-05-18.
//
// Originally added in Sprint UI-10, removed in PR #243 ("never changed
// mid-conversation"), restored 2026-05-18 because both the new-agent
// flow and mid-chat switches are legitimate: the mode is picked up-front
// per conversation, but the founder may still flip it later (the change
// persists for the rest of that conversation via agents.permissionMode
// + respawn). The respawn is why ContinueComposer wraps onChange in a
// confirm dialog; NewComposer renders the chip raw (nothing to respawn).
//
// Sprint mode-trust-mode-truth-up (2026-06-04): the picker no longer
// exposes 'auto'. 'auto' is kept as a runtime alias of 'agent' (same
// CLI flag + same hook) so legacy chips that still carry mode='auto'
// render the same label/icon. The MODE_ICON map keeps the 'auto' key
// for that reason.
//
// shadcn DropdownMenu trigger. Items with icon + label + one-line
// description. i18n strings live at modeChip.{agent,plan,auto} + *Desc;
// reused from SettingsPage (single source of truth for the labels).

import type { PermissionMode } from '../types';
import { useTranslation } from '../i18n';
import { Icon, type IconName } from './atoms/Icon';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export interface ModeChipProps {
  mode: PermissionMode;
  onChange: (next: PermissionMode) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

// MODE_ICON keeps the 'auto' key so legacy chips that still carry
// mode='auto' (post-Sprint-mode-trust-mode-truth-up alias) render the
// same Infinity icon as 'agent'.
const MODE_ICON: Record<PermissionMode, IconName> = {
  agent: 'Infinity',
  plan: 'BookOpen',
  auto: 'Infinity',
};

// Ordered to match SettingsPage. 'auto' is intentionally NOT in this
// list — it is a runtime alias of 'agent' and is hidden from the picker.
const MODES: { value: PermissionMode; icon: IconName }[] = [
  { value: 'agent', icon: 'Infinity' },
  { value: 'plan', icon: 'BookOpen' },
];

export function ModeChip({ mode, onChange, disabled, size = 'sm' }: ModeChipProps) {
  const { t } = useTranslation();
  const fontClass = size === 'md' ? 'text-body' : 'text-chip';
  const label = t(`modeChip.${mode}` as const);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('modeChip.modeAriaLabel', { mode: label })}
          className={`${fontClass} gap-1 h-6 px-2 text-muted-foreground hover:text-foreground ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ minHeight: 'auto', minWidth: 'auto' }}
          data-testid="mode-chip"
          data-mode={mode}
        >
          <Icon
            name={MODE_ICON[mode]}
            size={12}
            className="shrink-0 text-muted-foreground/70"
            aria-hidden
          />
          <span>{label}</span>
          <Icon name="ChevronDown" size={10} className="shrink-0 text-muted-foreground/70" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" data-testid="mode-popover">
        {MODES.map((m) => {
          const itemLabel = t(`modeChip.${m.value}` as const);
          const desc = t(`modeChip.${m.value}Desc` as const);
          return (
            <DropdownMenuItem
              key={m.value}
              onClick={() => onChange(m.value)}
              data-testid={`mode-option-${m.value}`}
            >
              <div className="flex items-start gap-2">
                <Icon name={m.icon} size={14} className="shrink-0 mt-0.5 text-muted-foreground" aria-hidden />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{itemLabel}</span>
                  <span className="text-xs text-muted-foreground">{desc}</span>
                </div>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
